import React, { useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, Container } from '@mui/material';
import Welcome from './components/Welcome';
import Quiz from './components/Quiz';
import Result from './components/Result';
import { PersonalityType } from './data/mbtiTypes';

interface Scores {
  I: number; E: number;
  S: number; N: number;
  T: number; F: number;
  J: number; P: number;
}

const theme = createTheme({
  palette: {
    primary:    { main: '#2D1B10' },
    secondary:  { main: '#F5A623' },
    background: { default: '#FAFAF7', paper: '#FFFFFF' },
  },
  typography: {
    fontFamily: '"Nunito", "Noto Sans TC", "Helvetica", "Arial", sans-serif',
    h4: { fontFamily: '"DM Serif Display", Georgia, serif', fontStyle: 'italic' },
    h5: { fontFamily: '"DM Serif Display", Georgia, serif', fontStyle: 'italic' },
    h6: { fontFamily: '"DM Serif Display", Georgia, serif', fontStyle: 'italic' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: '#2D1B10',
          '&:hover': { background: '#4A2D1A' },
        },
      },
    },
  },
});

// ── 品牌 Navbar ──────────────────────────────────────────────────────────────
function BrandNav() {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky', top: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 2, sm: 3 }, height: 56,
        background: 'rgba(250,250,247,0.9)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(107,76,59,0.08)',
      }}
    >
      <a
        href="https://smallfatmao.com"
        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
      >
        <img
          src="/maologo.jpg"
          alt="小胖毛"
          style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
        />
        <span style={{
          fontFamily: '"DM Serif Display", Georgia, serif',
          fontStyle: 'italic',
          fontSize: 17,
          color: '#2D1B10',
          letterSpacing: '0.01em',
        }}>
          Smallfatmao
        </span>
      </a>
      <span style={{
        fontSize: 12,
        fontWeight: 700,
        color: '#6B4C3B',
        letterSpacing: '0.1em',
        background: 'rgba(245,166,35,0.12)',
        border: '1px solid rgba(245,166,35,0.35)',
        borderRadius: 999,
        padding: '3px 10px',
      }}>
        MaoMBTI
      </span>
    </Box>
  );
}

const App: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'welcome' | 'quiz' | 'result'>('welcome');
  const [catName, setCatName] = useState('');
  const [scores, setScores] = useState<Scores>({ I:0,E:0,S:0,N:0,T:0,F:0,J:0,P:0 });
  const [result, setResult] = useState<PersonalityType>('ESTJ');

  const calculateMBTIType = (s: Scores): PersonalityType =>
    [s.I>s.E?'I':'E', s.S>s.N?'S':'N', s.T>s.F?'T':'F', s.J>s.P?'J':'P'].join('') as PersonalityType;

  function gtag(event: string, params?: Record<string, string | number>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (typeof w.gtag === 'function') w.gtag('event', event, params ?? {});
  }

  const handleStart = (name: string) => {
    setCatName(name);
    gtag('catmbti_quiz_started');
    setCurrentStep('quiz');
  };
  const handleQuizComplete = (s: Scores) => {
    const type = calculateMBTIType(s);
    setResult(type);
    gtag('catmbti_result_viewed', { mbti_type: type });
    setCurrentStep('result');
  };
  const handleRestart      = () => {
    setCurrentStep('welcome');
    setCatName('');
    setScores({ I:0,E:0,S:0,N:0,T:0,F:0,J:0,P:0 });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', background: '#FAFAF7' }}>
        <BrandNav />
        <Container maxWidth="md">
          <Box sx={{ pt: 3, pb: 6 }}>
            {currentStep === 'welcome' ? (
              <Welcome onStart={handleStart} />
            ) : currentStep === 'quiz' ? (
              <Quiz onComplete={handleQuizComplete} />
            ) : (
              <Result type={result} catName={catName} onRestart={handleRestart} />
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
